import { useState } from 'react'
import { ChevronDown, Layers, Database, Cloud, Bookmark, ExternalLink, AlertTriangle } from 'lucide-react'
import type { Citation, SourceKind, TraceStep, TurnTrace } from '../../types/chat'

/**
 * Where an answer came from, under the answer.
 *
 * Collapsed to a row of pills so the conversation stays readable; expanded, every
 * step with what it actually touched. Nothing here is declared — the tables, hosts and
 * chunks were all observed server-side while the turn ran.
 *
 * Source pills are deliberately NOT colour-coded. Emerald, orange and coral are RAG
 * status colours under the brand guide, and a source is a category rather than a
 * state. The one place coral appears is a step that failed, where it is status.
 */

const SOURCE_META: Record<SourceKind, { label: string; Icon: typeof Layers }> = {
  brain: { label: 'Company Brain', Icon: Layers },
  database: { label: 'Database', Icon: Database },
  external: { label: 'External', Icon: Cloud },
  memory: { label: 'Memories', Icon: Bookmark },
  model: { label: 'Model', Icon: Layers },
}

/** Hostnames said the way a person would recognise them. */
const HOST_LABEL: Record<string, string> = {
  'graph.microsoft.com': 'Microsoft 365',
  'api.xero.com': 'Xero',
  'api.bufferapp.com': 'Buffer',
  'api.short.io': 'Short.io',
  'api.annature.com.au': 'Annature',
}

function ms(value: number): string {
  return value >= 1000 ? `${(value / 1000).toFixed(2)}s` : `${Math.round(value)}ms`
}

/** Count per source across the turn, ordered so retrieval reads before plumbing. */
const ORDER: SourceKind[] = ['brain', 'memory', 'database', 'external']

function tally(steps: TraceStep[]): Array<{ kind: SourceKind; count: number; failed: boolean }> {
  const counts = new Map<SourceKind, { count: number; failed: boolean }>()
  for (const step of steps) {
    for (const kind of step.sources) {
      const prev = counts.get(kind) ?? { count: 0, failed: false }
      // A Brain step counts its chunks, everything else counts as one touch. Six
      // chunks from one search is the honest number for what was consulted.
      const add = kind === 'brain' ? step.citations?.length || 1 : 1
      counts.set(kind, { count: prev.count + add, failed: prev.failed || !!step.error })
    }
  }
  return ORDER.filter((k) => counts.has(k)).map((kind) => ({ kind, ...counts.get(kind)! }))
}

function CitationRow({ cite }: { cite: Citation }) {
  const body = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1 truncate text-[11.5px] font-medium text-foreground">
          <span className="truncate">{cite.title}</span>
          {cite.url && <ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-60" aria-hidden />}
        </span>
        {typeof cite.score === 'number' && (
          <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
            {cite.score.toFixed(3)}
          </span>
        )}
      </div>
      {cite.detail && <div className="font-mono text-[10px] text-muted-foreground">{cite.detail}</div>}
      {cite.snippet && (
        <div className="mt-1 border-t border-dashed pt-1 text-[11px] leading-snug text-muted-foreground">
          {cite.snippet}
        </div>
      )}
    </>
  )

  const shell = 'block rounded-r border border-l-2 border-l-sky-500 bg-card/60 px-2 py-1.5'

  // An external document opens in a new tab; a chunk with no source document is not
  // a link at all, rather than a link that goes nowhere.
  return cite.url ? (
    <a href={cite.url} target="_blank" rel="noreferrer" className={`${shell} transition-colors hover:bg-muted/60`}>
      {body}
    </a>
  ) : (
    <div className={shell}>{body}</div>
  )
}

function StepRow({ step }: { step: TraceStep }) {
  const failed = !!step.error
  const Icon = SOURCE_META[step.sources[0] ?? 'database'].Icon

  // One line naming what the step touched, built only from what was observed.
  const facts: string[] = []
  if (step.tables?.length) facts.push(step.tables.join(', '))
  if (step.queries) facts.push(`${step.queries} ${step.queries === 1 ? 'query' : 'queries'}`)
  for (const h of step.hosts ?? []) facts.push(`${HOST_LABEL[h.host] ?? h.host} · ${h.status}`)
  if (step.citations?.length) facts.unshift(`${step.citations.length} chunks`)
  if (typeof step.rows === 'number' && !step.citations?.length) {
    facts.push(`${step.rows} ${step.rows === 1 ? 'row' : 'rows'}`)
  }

  const slowest = step.calls.reduce((max, c) => Math.max(max, c.ms), 0)

  return (
    <li className="relative border-l pl-4 pb-2.5 last:border-l-transparent last:pb-0">
      <span
        aria-hidden
        className={`absolute -left-[3.5px] top-1.5 h-[7px] w-[7px] rounded-full border-[1.5px] bg-background ${
          failed ? 'border-destructive' : 'border-sky-500'
        }`}
      />
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] text-foreground">
          {step.n}. {step.label}
        </span>
        {slowest > 0 && (
          <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">{ms(slowest)}</span>
        )}
      </div>

      {facts.length > 0 && (
        <div
          className={`mt-0.5 flex items-center gap-1.5 overflow-x-auto font-mono text-[10px] ${
            failed ? 'text-destructive' : 'text-muted-foreground'
          }`}
        >
          <Icon className={`h-2.5 w-2.5 shrink-0 ${failed ? '' : 'text-sky-500'}`} aria-hidden />
          <span className="whitespace-nowrap">{facts.join(' · ')}</span>
        </div>
      )}

      {step.citations?.length ? (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {step.citations.map((c, i) => (
            <CitationRow key={`${c.title}-${i}`} cite={c} />
          ))}
        </div>
      ) : null}

      {step.error && (
        <div className="mt-1 flex items-start gap-1.5 text-[11px] text-destructive">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>{step.error}</span>
        </div>
      )}
    </li>
  )
}

export function MessageTrace({ trace }: { trace: TurnTrace | undefined }) {
  const [open, setOpen] = useState(false)

  // No record at all — a message from before this shipped. Render nothing rather than
  // claiming the answer used no sources; those are different things.
  if (!trace) return null

  const pills = tally(trace.steps)

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-1.5 rounded text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        {pills.length > 0 ? (
          pills.map(({ kind, count, failed }) => {
            const { label, Icon } = SOURCE_META[kind]
            return (
              <span
                key={kind}
                className={`inline-flex items-center gap-1 rounded-full border bg-background px-1.5 py-px text-[10.5px] text-muted-foreground ${
                  failed ? 'border-destructive/40' : ''
                }`}
              >
                <Icon className={`h-2.5 w-2.5 ${failed ? 'text-destructive' : 'text-sky-500'}`} aria-hidden />
                {label}
                <b className={`font-mono font-semibold tabular-nums ${failed ? 'text-destructive' : 'text-foreground'}`}>
                  {count}
                </b>
              </span>
            )
          })
        ) : (
          // The finding, not an empty state. Every confabulation this subsystem has
          // had looked exactly like this: a confident answer with nothing behind it.
          <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            No sources. Answered from the model only.
          </span>
        )}

        <span className="ml-auto inline-flex items-center gap-0.5 text-[10.5px] text-muted-foreground">
          trace
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
        </span>
      </button>

      {open && (
        <div className="mt-2 pl-1">
          {trace.steps.length > 0 ? (
            <ol className="ml-[3px]">
              {trace.steps.map((step) => (
                <StepRow key={step.n} step={step} />
              ))}
            </ol>
          ) : (
            <p className="text-[11px] leading-snug text-muted-foreground">
              Nothing was read, so there is no trail to open. Anything factual in this reply is unverified.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
